import React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { format, isValid } from "date-fns";
import RightworkSelect from "../../../../../components/shared/RightworkSelect";
import PayClassificationField, {
  LABOR_EXEMPT_FOR,
  PENDING_EXEMPT_STATUS_FOR,
  classificationOf,
  payTypeOf,
  payTypeOfClassification,
} from "./PayClassificationField";
import { schedulingFieldSx } from "./EmployeeSchedulingPreferences";
import { postRightworkAPI } from "../../utilities/requests";
import { API_post_store_team_member_labor_exempt } from "../../../../../constants";

export const SALARY_INTERVAL_OPTIONS = [
  { value: "annual", label: "Per year" },
  { value: "monthly", label: "Per month" },
  { value: "per_pay_period", label: "Per pay period" },
];

const datePickerSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "white",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#6d28d9" },
    "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
  },
};

// The form's starting point: the saved classification and the current
// all-roles pay (Rightwork wages only).
function savedState(wageData) {
  const classification = classificationOf(payTypeOf(wageData), wageData?.labor_exempt, wageData?.wage_type);
  const wage = wageData?.pay_source === "rightwork" ? wageData?.current_wage : null;
  return {
    classification,
    hourlyRate: wage?.compensation_type === "hourly" ? String(wage.hourly_rate ?? "") : "",
    salaryAmount: wage?.compensation_type === "salary" ? String(wage.salary_amount ?? "") : "",
    salaryInterval: wage?.compensation_type === "salary" ? wage.salary_interval || "annual" : "annual",
  };
}

/**
 * The Pay card's form: pay classification and, with Rightwork wages, the pay
 * rate. One Save. An exempt-only change saves now; a pay type or rate change
 * saves a new all-roles wage that takes effect on the chosen date, carrying
 * the classification with it.
 */
export default function EmployeePayForm({
  wageData,
  memberId,
  homeStore,
  defaultEffectiveDate,
  readOnlyPay,
  upcomingPay,
  onSaveWage,
  onSaved,
}) {
  const id = React.useId();
  const editable = wageData?.pay_source === "rightwork";
  const saved = React.useMemo(() => savedState(wageData), [wageData]);
  const [form, setForm] = React.useState(saved);
  const [effectiveDate, setEffectiveDate] = React.useState(defaultEffectiveDate || "");
  // Once the admin picks a date, a later default (pay periods load
  // separately) mustn't replace it.
  const [dateTouched, setDateTouched] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [status, setStatus] = React.useState(null); // "saved" | error message

  // Take refreshed saved values only while the form has no edits (or for a
  // different employee), so a refresh landing mid-edit doesn't wipe it.
  const lastSaved = React.useRef(saved);
  const lastMember = React.useRef(memberId);
  React.useEffect(() => {
    setForm((current) => {
      const pristine = JSON.stringify(current) === JSON.stringify(lastSaved.current);
      const memberChanged = lastMember.current !== memberId;
      lastSaved.current = saved;
      lastMember.current = memberId;
      return pristine || memberChanged ? saved : current;
    });
  }, [saved, memberId]);
  React.useEffect(() => {
    if (!dateTouched) setEffectiveDate(defaultEffectiveDate || "");
  }, [defaultEffectiveDate, dateTouched]);
  const resetEffectiveDate = () => {
    setDateTouched(false);
    setEffectiveDate(defaultEffectiveDate || "");
  };
  // A different employee (or location) starts from a clean form.
  React.useEffect(() => {
    resetEffectiveDate();
    setStatus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const payType = payTypeOfClassification(form.classification);
  const typeChanged = payType !== payTypeOfClassification(saved.classification);
  // Compare amounts as numbers, so "21" over a saved "21.00" isn't a change.
  const sameAmount = (left, right) => (
    String(left).trim() === "" || String(right).trim() === ""
      ? String(left).trim() === String(right).trim()
      : Number(left) === Number(right)
  );
  const rateChanged = editable && (
    payType === "hourly"
      ? !sameAmount(form.hourlyRate, saved.hourlyRate)
      : !sameAmount(form.salaryAmount, saved.salaryAmount) || form.salaryInterval !== saved.salaryInterval
  );
  const newWage = editable && (typeChanged || rateChanged);
  const classificationChanged = form.classification !== saved.classification;
  const dirty = newWage || classificationChanged;

  const update = (changes) => {
    setStatus(null);
    setForm((current) => ({ ...current, ...changes }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!dirty || saving) return;
    if (newWage) {
      if (payType === "hourly" && !(Number(form.hourlyRate) > 0)) {
        setStatus("Enter an hourly rate.");
        return;
      }
      if (payType === "salary" && !(Number(form.salaryAmount) > 0)) {
        setStatus("Enter a salary amount.");
        return;
      }
      if (!effectiveDate) {
        setStatus("Choose when this pay takes effect.");
        return;
      }
    }
    setSaving(true);
    setStatus(null);
    try {
      let ok = true;
      if (newWage) {
        ok = await onSaveWage({
          store_team_member_id: memberId,
          effective_date: effectiveDate,
          store_team_member_role_id: null,
          compensation_type: payType,
          hourly_rate: payType === "hourly" ? form.hourlyRate : null,
          salary_amount: payType === "salary" ? form.salaryAmount : null,
          salary_interval: payType === "salary" ? form.salaryInterval : null,
          notes: null,
          wage_uuid: null,
          pending_exempt_status: PENDING_EXEMPT_STATUS_FOR[form.classification],
        });
      } else {
        await postRightworkAPI(API_post_store_team_member_labor_exempt, {
          store_id: homeStore,
          store_team_member_id: memberId,
          labor_exempt: LABOR_EXEMPT_FOR[form.classification],
        });
      }
      if (ok) {
        if (newWage) {
          // Back to the saved baseline: a future-dated change shows as
          // upcoming pay rather than staying as an unsaved edit.
          setForm(saved);
          resetEffectiveDate();
          onSaved?.();
        } else {
          // An exempt-only change applies now: it is the new saved value.
          onSaved?.({ laborExempt: LABOR_EXEMPT_FOR[form.classification] });
        }
        setStatus("saved");
      }
    } catch (error) {
      setStatus(error?.response?.data?.system?.message || "Pay could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const effectiveDateValue = effectiveDate ? new Date(`${effectiveDate}T00:00:00`) : null;

  return (
    <form onSubmit={submit} noValidate>
      <div className="space-y-6 px-6 py-5">
        <PayClassificationField
          id={`${id}-classification`}
          value={form.classification}
          current={saved.classification}
          source={wageData?.pay_source}
          disabled={saving}
          onChange={(classification) => update({ classification })}
        />

        {editable ? (
          payType === "hourly" ? (
            <div className="max-w-sm">
              <label htmlFor={`${id}-hourly`} className="mb-2 block text-sm font-medium text-slate-900">Hourly rate</label>
              <TextField id={`${id}-hourly`} fullWidth type="number" size="small" placeholder="e.g. 18.50"
                inputProps={{ min: 0, step: "0.01" }} sx={schedulingFieldSx} disabled={saving}
                value={form.hourlyRate} onChange={(event) => update({ hourlyRate: event.target.value })} />
            </div>
          ) : (
            <div>
              <span id={`${id}-salary-label`} className="mb-2 block text-sm font-medium text-slate-900">Salary amount</span>
              <div className="flex max-w-md flex-wrap gap-3">
                <div className="min-w-[160px] flex-1">
                  <TextField id={`${id}-salary`} fullWidth type="number" size="small" placeholder="e.g. 65000"
                    inputProps={{ min: 0, step: "0.01", "aria-labelledby": `${id}-salary-label` }}
                    sx={schedulingFieldSx} disabled={saving}
                    value={form.salaryAmount} onChange={(event) => update({ salaryAmount: event.target.value })} />
                </div>
                <div className="w-44">
                  <label id={`${id}-interval-label`} htmlFor={`${id}-interval`} className="sr-only">Salary interval</label>
                  <RightworkSelect id={`${id}-interval`} value={form.salaryInterval} disabled={saving}
                    options={SALARY_INTERVAL_OPTIONS}
                    onChange={(event) => update({ salaryInterval: event.target.value })} />
                </div>
              </div>
            </div>
          )
        ) : (
          <div>
            <span className="mb-1 block text-sm font-medium text-slate-900">Pay rate</span>
            <p className="text-sm text-slate-600">{readOnlyPay}</p>
          </div>
        )}

        {upcomingPay && !newWage && (
          <p className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">{upcomingPay}</p>
        )}

        {newWage && (
          <div className="max-w-sm">
            <label htmlFor={`${id}-effective`} className="mb-2 block text-sm font-medium text-slate-900">Takes effect</label>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                value={effectiveDateValue}
                onChange={(next) => {
                  setStatus(null);
                  setDateTouched(true);
                  setEffectiveDate(next && isValid(next) ? format(next, "yyyy-MM-dd") : "");
                }}
                format="MM/dd/yyyy"
                disabled={saving}
                slotProps={{
                  textField: { id: `${id}-effective`, fullWidth: true, size: "small", sx: datePickerSx },
                  day: { sx: { "&.MuiPickersDay-root.Mui-selected": { backgroundColor: "#6d28d9" } } },
                }}
              />
            </LocalizationProvider>
            <p className="mt-2 text-sm leading-5 text-slate-500">
              New pay starts with the first pay period on or after this date.
              {classificationChanged && " The new classification starts then too."}
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
        {status === "saved" && <p role="status" className="mr-auto text-sm text-emerald-700">Pay saved.</p>}
        {status && status !== "saved" && (
          <p role="alert" className="mr-auto text-sm text-red-700">{status}</p>
        )}
        {dirty && !saving && (
          <Button type="button" variant="text" onClick={() => { setForm(saved); resetEffectiveDate(); setStatus(null); }}
            sx={{ fontFamily: "inherit", textTransform: "none", color: "#475569" }}>
            Discard changes
          </Button>
        )}
        <Button type="submit" variant="contained" disableElevation disabled={!dirty || saving}
          sx={{ fontFamily: "inherit", textTransform: "none", borderRadius: "8px", paddingX: "20px", paddingY: "8px", backgroundColor: "#6d28d9", "&:hover": { backgroundColor: "#5b21b6" } }}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}
